// Browser pop-up notifications control for the messagerie (web).
//
// Bell toggle (on/off/denied) plus a small dismissible discoverability prompt
// shown when pop-ups are off. Backed by the per-device FCM token: "on"
// registers this browser's token so heric-back pushes new-message
// notifications, "off" removes it. Styled for the light MessagerieScreen header.

import { useState } from "react";
import Icon1 from "@assets/icons/Icon";
import { useWebPushNotifications } from "@services/notifications/webPush";

const PROMPT_DISMISS_KEY = "heric_messagerie_webpush_prompt_dismissed";

/** Bell toggle for the messagerie header. Hidden when loading/unsupported. */
export function NotificationsBell() {
  const { status, working, enable, disable } = useWebPushNotifications();

  if (status === "loading" || status === "unsupported") return null;

  const on = status === "on";
  const denied = status === "denied";
  const title = denied
    ? "Notifications bloquées par le navigateur — autorisez-les dans les réglages du site"
    : on
      ? "Notifications pop-up activées — cliquer pour désactiver"
      : "Notifications pop-up désactivées — cliquer pour activer";

  return (
    <button
      onClick={() => {
        if (denied || working) return;
        if (on) disable();
        else enable();
      }}
      title={title}
      className="hover:bg-neutral-100 rounded-md flex items-center justify-center"
      style={{
        width: 32,
        height: 32,
        opacity: working ? 0.5 : 1,
        cursor: denied ? "help" : "pointer",
      }}
    >
      <Icon1 name="cloche" size={16} color={on ? "#6366f1" : "#A1A1A1"} />
    </button>
  );
}

/**
 * Discoverability banner shown when pop-ups are off (and not dismissed).
 * Hooks into the same web-push state so the "Activer" button mirrors the bell.
 */
export function NotificationsPrompt() {
  const { status, working, enable } = useWebPushNotifications();
  const [dismissed, setDismissed] = useState(
    () =>
      typeof window !== "undefined" &&
      localStorage.getItem(PROMPT_DISMISS_KEY) === "1",
  );

  if (dismissed) return null;
  if (status !== "off" && status !== "denied") return null;

  const denied = status === "denied";

  return (
    <div
      className="flex flex-row items-start"
      style={{ gap: 10, padding: "10px 12px", background: "#EEF0FF", borderRadius: 10 }}
    >
      <span style={{ marginTop: 1 }}>
        <Icon1 name="cloche" size={16} color="#6366f1" />
      </span>
      <div className="flex flex-col" style={{ flex: 1, gap: 6 }}>
        <span
          style={{
            fontFamily: "Poppins",
            fontSize: 12,
            lineHeight: "16px",
            color: "#4338CA",
          }}
        >
          {denied
            ? "Les notifications sont bloquées par votre navigateur. Autorisez-les dans les réglages du site pour recevoir un pop-up à chaque nouveau message, même sur un autre onglet."
            : "Activez les pop-up pour être prévenu des nouveaux messages, même quand vous êtes sur un autre onglet."}
        </span>
        {!denied && (
          <button
            onClick={enable}
            disabled={working}
            style={{
              alignSelf: "flex-start",
              fontFamily: "Poppins",
              fontWeight: 600,
              fontSize: 12,
              color: "#FFFFFF",
              background: "#6366f1",
              padding: "6px 12px",
              borderRadius: 8,
              opacity: working ? 0.6 : 1,
            }}
          >
            {working ? "Activation…" : "Activer les notifications"}
          </button>
        )}
      </div>
      <button
        onClick={() => {
          setDismissed(true);
          try {
            localStorage.setItem(PROMPT_DISMISS_KEY, "1");
          } catch {
            /* ignore storage errors (private mode) */
          }
        }}
        title="Ne plus afficher"
        className="hover:bg-white rounded-md flex items-center justify-center flex-shrink-0"
        style={{ width: 22, height: 22 }}
      >
        <Icon1 name="close" size={10} color="#7C81E8" />
      </button>
    </div>
  );
}
